import axios from "../general/HttpClient";

export interface OrphanedUserSummary {
    id: string;
    userName: string;
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    fullName: string;
    isDisabled: boolean;
    dateCreated: string;
    dateUpdated: string;
    lastLoginDate: string;
    providerLoginCount: number;
    claimCount: number;
    disabledMembershipCount: number;
    metadataCount: number;
    groupCount: number;
    delegatedAccessCount: number;
    credentialCount: number;
    relatedLocalRowCount: number;
    hasEnabledMemberships: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isCurrentUser: boolean;
    isRecentAccount: boolean;
    canDelete: boolean;
    protectionReasons: string[];
}

export interface OrphanedUsersListResponse {
    users: OrphanedUserSummary[];
    total: number;
    skip: number;
    limit: number;
}

export interface OrphanedUserMembershipDetail {
    organizationId: string;
    organizationName: string;
    type: string;
    enabled: boolean;
    source: string;
    sourceId: string;
}

export interface OrphanedUserProviderLoginDetail {
    loginProvider: string;
    providerKey: string;
    providerDisplayName: string;
}

export interface OrphanedUserClaimDetail {
    id: number;
    claimType: string;
    claimValue: string;
}

export interface OrphanedUserMetadataDetail {
    id: string;
    organizationId: string;
    key: string;
    enableOverride: boolean;
    hasValue: boolean;
    hasOverrideValue: boolean;
    value: string;
    overrideValue: string;
}

export interface OrphanedUserGroupDetail {
    groupId: string;
    groupName: string;
    organizationId: string;
    type: string;
    membershipType: string;
    source: string;
    sourceId: string;
}

export interface OrphanedUserDelegatedAccessDetail {
    direction: string;
    delegatorId: string;
    delegateeId: string;
    delegationRules: string;
}

export interface OrphanedUserCredentialDetail {
    id: string;
    deviceId: string;
    credentialType: string;
    registrationDate: string;
}

export interface OrphanedUserRelatedLocalRowCount {
    name: string;
    count: number;
}

export interface OrphanedUserDetail extends OrphanedUserSummary {
    isOrphanedUser: boolean;
    enabledMembershipCount: number;
    metadataValuesIncluded: boolean;
    enabledMemberships: OrphanedUserMembershipDetail[];
    disabledMemberships: OrphanedUserMembershipDetail[];
    providerLogins: OrphanedUserProviderLoginDetail[];
    claims: OrphanedUserClaimDetail[];
    metadata: OrphanedUserMetadataDetail[];
    groups: OrphanedUserGroupDetail[];
    delegatedAccess: OrphanedUserDelegatedAccessDetail[];
    credentials: OrphanedUserCredentialDetail[];
    relatedLocalRows: OrphanedUserRelatedLocalRowCount[];
}

export interface DeleteOrphanedUserResult {
    userId: string;
    success: boolean;
    deleted: boolean;
    notFound: boolean;
    message: string;
    protectionReasons: string[];
    detail: OrphanedUserDetail;
}

export function ListOrphanedUsers(query: string, skip: number, limit: number): Promise<OrphanedUsersListResponse> {
    return axios
        .get("/api/DataMaintenance/ListOrphanedUsers", {
            params: {
                query,
                skip,
                limit,
            },
        })
        .then((response) => response.data);
}

export function DeleteOrphanedUser(userId: string): Promise<DeleteOrphanedUserResult> {
    return axios
        .delete("/api/DataMaintenance/DeleteOrphanedUser", {
            params: {
                userId,
            },
        })
        .then((response) => response.data);
}

export function GetOrphanedUser(userId: string, includeMetadataValues = false): Promise<OrphanedUserDetail> {
    return axios
        .get("/api/DataMaintenance/GetOrphanedUser", {
            params: {
                userId,
                includeMetadataValues,
            },
        })
        .then((response) => response.data);
}
